import type { Branch, DashboardData, HaciendaSettings, Member, UserPreference } from '@facture/contracts'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  ArrowDownRightIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  Building2Icon,
  CheckCircle2Icon,
  CircleIcon,
  FileTextIcon,
  GitBranchIcon,
  InboxIcon,
  KeyRoundIcon,
  PlugZapIcon,
  PlusIcon,
  ReceiptIcon,
  SparklesIcon,
  UsersIcon,
  WalletIcon,
  WalletMinimalIcon,
  XIcon,
} from 'lucide-react'
import { AnimatePresence, m } from 'motion/react'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import {
  AnimatedNumber,
  Collapse,
  DURATION,
  FadeIn,
  STAGGER,
  Stagger,
  StaggerItem,
  TRANSITION,
  useChartMotion,
} from '@/components/motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Page } from '@/components/ui/page'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { HaciendaStatusIcon, KIND_LABEL } from '@/features/documents/status'
import { CertificateAlert } from '@/features/settings/certificate-alert'
import { api } from '@/lib/api'
import { useChartColors } from '@/lib/chart-colors'
import { formatCompact, formatDate, formatMoney, formatRelative } from '@/lib/format'
import { companyQuery } from '@/lib/queries'
import { useActiveMembership, useCan, useSession } from '@/lib/session'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/')({
  staticData: { crumb: 'Inicio' },
  component: Dashboard,
})

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic']

function greeting() {
  const hour = new Date().getHours()
  if (hour < 12) return 'Buenos días'
  if (hour < 19) return 'Buenas tardes'
  return 'Buenas noches'
}

function Dashboard() {
  const user = useSession((s) => s.user)
  const membership = useActiveMembership()
  const canIssue = useCan('documents:issue')
  const [branchId, setBranchId] = useState('all')
  const branches = useQuery({ queryKey: ['branches'], queryFn: () => api.get<Branch[]>('/branches') })
  const failedAlert = useDismissedSince('failed', membership?.companyId)
  const receptionsAlert = useDismissedSince('receptions', membership?.companyId)
  const dashboard = useQuery({
    queryKey: ['dashboard', branchId, failedAlert.since, receptionsAlert.since],
    queryFn: () =>
      api.get<DashboardData>('/dashboard', {
        query: {
          ...(branchId === 'all' ? {} : { branchId }),
          ...(failedAlert.since ? { failedSince: failedAlert.since } : {}),
          ...(receptionsAlert.since ? { receptionsSince: receptionsAlert.since } : {}),
        },
      }),
    refetchInterval: 60_000,
    // Al cambiar de sucursal o cerrar un aviso se conservan los datos anteriores: los montos cuentan hasta el
    // valor nuevo en vez de volver al esqueleto.
    placeholderData: keepPreviousData,
  })
  const d = dashboard.data
  // Un aviso cerrado se oculta en el acto (colapsando); vuelve a evaluarse con los datos frescos.
  const [hidden, setHidden] = useState<{ failed?: boolean; receptions?: boolean }>({})
  // biome-ignore lint/correctness/useExhaustiveDependencies: se reinicia con cada respuesta nueva del panel
  useEffect(() => {
    if (!dashboard.isPlaceholderData) setHidden({})
  }, [dashboard.dataUpdatedAt, dashboard.isPlaceholderData])
  const showFailed = !!d && d.kpis.failedDocuments > 0 && !hidden.failed
  const showReceptions = !!d && d.kpis.pendingReceptions > 0 && !hidden.receptions
  const growth =
    d && d.kpis.salesPrevMonth > 0
      ? ((d.kpis.salesMonth - d.kpis.salesPrevMonth) / d.kpis.salesPrevMonth) * 100
      : null

  return (
    <Page>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-muted-foreground text-xs">
            <SparklesIcon className="size-3.5 text-primary" /> {formatDate(new Date(), "EEEE d 'de' MMMM")}
          </div>
          <h1 className="font-semibold text-2xl tracking-tight sm:text-3xl">
            {greeting()}, <span className="text-brand-gradient">{user?.name.split(' ')[0]}</span>
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            Así va{' '}
            <span className="font-medium text-foreground">
              {membership?.tradeName ?? membership?.companyName}
            </span>{' '}
            este mes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(branches.data?.length ?? 0) > 1 && (
            <Select
              value={branchId}
              onValueChange={setBranchId}
              className="h-9 w-52"
              options={[
                { value: 'all', label: 'Todas las sucursales' },
                ...(branches.data ?? []).map((b) => ({ value: b.id, label: `${b.code} · ${b.name}` })),
              ]}
            />
          )}
          {canIssue && (
            <div className="flex gap-2">
              <Button asChild variant="outline">
                <Link to="/documents/new" search={{ kind: 'TE' }}>
                  <ReceiptIcon /> Tiquete
                </Link>
              </Button>
              <Button asChild>
                <Link to="/documents/new" search={{ kind: 'FE' }}>
                  <PlusIcon /> Nueva factura
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>

      <CertificateAlert className="mb-6" />

      {membership?.plan === 'api' && (
        <Link
          to="/api-activity"
          className="mb-6 flex items-start gap-3 rounded-xl border border-info/25 bg-info-soft px-4 py-3 text-info-soft-foreground text-sm transition hover:opacity-90"
        >
          <PlugZapIcon className="mt-0.5 size-4 shrink-0" />
          <span className="flex-1">
            Tu compañía tiene el <strong>plan API</strong>: tus sistemas emiten por{' '}
            <code>/api/documentos</code> y Facture firma, envía a Hacienda y manda el correo al cliente. Mirá
            la actividad de la API: llamadas, errores y estado de cada comprobante. Las credenciales y el
            ejemplo están en Configuración → API.
          </span>
          <span className="flex shrink-0 items-center gap-1 self-center font-medium">
            Ver actividad <ArrowRightIcon className="size-4" />
          </span>
        </Link>
      )}

      <SetupChecklist />

      {/* Avisos: entran y, al cerrarlos con la X, colapsan suavemente (el otro se reacomoda). */}
      <Collapse show={showFailed || showReceptions}>
        <div className="grid gap-3 pb-6 sm:grid-cols-2">
          <AnimatePresence initial={false} mode="popLayout">
            {showFailed && (
              <m.div key="failed" layout exit={{ opacity: 0, scale: 0.97 }} transition={TRANSITION.base}>
                <Alert
                  to="/invoices"
                  search={{ status: 'failed' }}
                  tone="warning"
                  icon={AlertTriangleIcon}
                  onDismiss={() => {
                    setHidden((h) => ({ ...h, failed: true }))
                    failedAlert.dismiss()
                  }}
                >
                  <strong>{d.kpis.failedDocuments}</strong> comprobante
                  {d.kpis.failedDocuments === 1 ? '' : 's'} con error o rechazado
                  {d.kpis.failedDocuments === 1 ? '' : 's'} por Hacienda
                </Alert>
              </m.div>
            )}
            {showReceptions && (
              <m.div key="receptions" layout exit={{ opacity: 0, scale: 0.97 }} transition={TRANSITION.base}>
                <Alert
                  to="/receptions"
                  search={{ status: 'pending' }}
                  tone="info"
                  icon={InboxIcon}
                  onDismiss={() => {
                    setHidden((h) => ({ ...h, receptions: true }))
                    receptionsAlert.dismiss()
                  }}
                >
                  <strong>{d.kpis.pendingReceptions}</strong> comprobante
                  {d.kpis.pendingReceptions === 1 ? '' : 's'} de proveedores por responder
                </Alert>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </Collapse>

      <Stagger className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Kpi
          label="Ventas del mes"
          value={d ? d.kpis.salesMonth : null}
          icon={FileTextIcon}
          hint={
            growth === null ? (
              `${d?.kpis.documentsMonth ?? 0} comprobantes`
            ) : (
              <span
                className={cn('flex items-center gap-1', growth >= 0 ? 'text-success' : 'text-destructive')}
              >
                {growth >= 0 ? (
                  <ArrowUpRightIcon className="size-3.5" />
                ) : (
                  <ArrowDownRightIcon className="size-3.5" />
                )}
                {Math.abs(growth).toFixed(1)}% vs. mes anterior
              </span>
            )
          }
          accent
        />
        <Kpi
          label="Cobrado del mes"
          value={d ? d.kpis.collectedMonth : null}
          icon={WalletIcon}
          hint="Cobros registrados, sin anulados"
          to="/payments"
        />
        <Kpi
          label="IVA facturado"
          value={d ? d.kpis.taxMonth : null}
          icon={ReceiptIcon}
          hint="Débito fiscal del mes"
        />
        <Kpi
          label="Por cobrar"
          value={d ? d.kpis.receivables : null}
          icon={WalletIcon}
          hint={
            d && d.kpis.overdue > 0 ? (
              <span className="text-destructive">{formatMoney(d.kpis.overdue)} vencido</span>
            ) : (
              'Todo al día'
            )
          }
          to="/payments"
        />
        <Kpi
          label="Gastos del mes"
          value={d ? d.kpis.expensesMonth : null}
          icon={WalletMinimalIcon}
          hint="Incluye compras aceptadas"
          to="/expenses"
        />
      </Stagger>

      <Stagger delay={0.12} className="mb-6 grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <StaggerItem>
          <Card className="h-full">
            <CardHeader>
              <div>
                <CardTitle>Ventas y gastos</CardTitle>
                <p className="mt-1 text-muted-foreground text-sm">Últimos 12 meses, en colones y sin IVA</p>
              </div>
            </CardHeader>
            <CardContent className="h-72">
              {d ? (
                <FadeIn className="size-full">
                  <MonthlyChart data={d.monthly} />
                </FadeIn>
              ) : (
                <Skeleton className="size-full" />
              )}
            </CardContent>
          </Card>
        </StaggerItem>
        <StaggerItem>
          <Card className="h-full">
            <CardHeader>
              <div>
                <CardTitle>Ritmo diario</CardTitle>
                <p className="mt-1 text-muted-foreground text-sm">
                  Ventas acumuladas: este mes vs. el anterior
                </p>
              </div>
            </CardHeader>
            <CardContent className="h-72">
              {d ? (
                <FadeIn className="size-full">
                  <DailyChart data={d.daily} />
                </FadeIn>
              ) : (
                <Skeleton className="size-full" />
              )}
            </CardContent>
          </Card>
        </StaggerItem>
      </Stagger>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>Gastos del mes por categoría</CardTitle>
            <Link
              to="/reports"
              search={{ report: 'expenses' }}
              className="text-primary text-xs hover:underline"
            >
              Ver reporte
            </Link>
          </CardHeader>
          <CardContent>
            <RankList
              items={(d?.expensesByCategory ?? []).map((c) => ({
                label: c.category,
                value: c.total,
                hint: 'Este mes',
              }))}
              empty="Sin gastos registrados este mes."
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Mejores clientes del año</CardTitle>
          </CardHeader>
          <CardContent>
            <RankList
              items={(d?.topCustomers ?? []).map((c) => ({
                label: c.name,
                value: c.total,
                hint: `${c.documents} comprobantes`,
              }))}
              empty="Todavía no hay ventas este año."
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Lo más vendido · 90 días</CardTitle>
          </CardHeader>
          <CardContent>
            <RankList
              items={(d?.topProducts ?? []).map((p) => ({
                label: p.description,
                value: p.total,
                hint: `${p.quantity} unidades`,
              }))}
              empty="Sin ventas en los últimos 90 días."
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Actividad reciente</CardTitle>
            <Link to="/invoices" className="text-primary text-xs hover:underline">
              Ver todo
            </Link>
          </CardHeader>
          <CardContent className="grid gap-1">
            {d?.recent.length === 0 && (
              <p className="text-muted-foreground text-sm">Aún no emitís comprobantes.</p>
            )}
            {d?.recent.map((r) => (
              <Link
                key={r.id}
                to="/documents/$id"
                params={{ id: r.id }}
                className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-accent/60"
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted font-semibold text-[10px] text-muted-foreground">
                  {KIND_LABEL[r.kind].short}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-sm">{r.receiverName ?? 'Consumidor final'}</div>
                  <div className="text-muted-foreground text-xs">
                    #{r.consecutive?.slice(10).replace(/^0+/, '')} · {formatRelative(r.issuedAt)}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="font-medium text-sm tabular">
                    {formatMoney(r.total, r.currency as 'CRC')}
                  </span>
                  <HaciendaStatusIcon status={r.haciendaStatus} />
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </Page>
  )
}

function Kpi({
  label,
  value,
  hint,
  icon: Icon,
  accent,
  to,
}: {
  label: string
  value: number | null
  hint?: ReactNode
  icon: typeof FileTextIcon
  accent?: boolean
  to?: '/payments' | '/expenses'
}) {
  const text = value === null ? '' : formatMoney(value)
  const body = (
    <Card
      className={cn(
        'relative h-full overflow-hidden p-5 transition',
        to && 'hover:border-foreground/15 hover:shadow-elevation-2',
      )}
    >
      {accent && (
        <div className="pointer-events-none absolute -top-16 -right-10 size-44 rounded-full bg-primary/20 blur-3xl" />
      )}
      <div className="relative">
        <div className="flex items-center justify-between text-muted-foreground text-sm">
          {label}
          <span
            className={cn(
              'flex size-8 items-center justify-center rounded-lg',
              accent ? 'bg-primary-soft text-primary' : 'bg-muted',
            )}
          >
            <Icon className="size-4" />
          </span>
        </div>
        {value === null ? (
          <Skeleton className="mt-3 h-8 w-40" />
        ) : (
          <div
            className={cn(
              'mt-2 truncate font-semibold tracking-tight tabular',
              text.length > 16 ? 'text-base' : text.length > 12 ? 'text-xl' : 'text-[26px]',
            )}
          >
            <AnimatedNumber value={value} format={formatMoney} />
          </div>
        )}
        {hint && <div className="mt-1 text-muted-foreground text-xs">{hint}</div>}
      </div>
    </Card>
  )
  return (
    <StaggerItem>
      {to ? (
        <Link to={to} className="block h-full">
          {body}
        </Link>
      ) : (
        body
      )}
    </StaggerItem>
  )
}

function Alert({
  to,
  search,
  tone,
  icon: Icon,
  onDismiss,
  children,
}: {
  to: '/invoices' | '/receptions'
  search: Record<string, string>
  tone: 'warning' | 'info'
  icon: typeof InboxIcon
  onDismiss: () => void
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-xl border pr-2 text-sm transition hover:brightness-105',
        tone === 'warning'
          ? 'border-warning/30 bg-warning-soft text-warning-soft-foreground'
          : 'border-info/25 bg-info-soft text-info-soft-foreground',
      )}
    >
      <Link to={to} search={search as never} className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4">
        <Icon className="size-5 shrink-0" />
        <span className="flex-1">{children}</span>
        <ArrowRightIcon className="size-4" />
      </Link>
      <button
        type="button"
        onClick={onDismiss}
        className="grid size-7 shrink-0 place-items-center rounded-md opacity-70 transition hover:bg-foreground/10 hover:opacity-100"
        aria-label="Cerrar aviso"
        title="Cerrar: solo volverá a aparecer si llegan nuevos"
      >
        <XIcon className="size-4" />
      </button>
    </div>
  )
}

/**
 * Instante en que se cerró un aviso, por usuario y compañía. Se guarda en el servidor (vale en cualquier equipo) y
 * en este navegador como caché inmediata; manda el cierre más reciente de los dos. El aviso vuelve solo si
 * aparecen elementos nuevos después de ese instante.
 */
function useDismissedSince(name: 'failed' | 'receptions', companyId: string | undefined) {
  const storageKey = `facture:dismissed:${name}:${companyId ?? 'none'}`
  const preference = `dashboard.dismissed.${name}`
  const read = () => {
    try {
      return localStorage.getItem(storageKey) ?? undefined
    } catch {
      return undefined
    }
  }
  const remember = (value: string) => {
    try {
      localStorage.setItem(storageKey, value)
    } catch {
      /* sin almacenamiento: queda el valor del servidor */
    }
  }
  const [since, setSince] = useState<string | undefined>(read)
  // Al cambiar de compañía se lee el valor de esa compañía.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `read` depende solo de storageKey
  useEffect(() => setSince(read()), [storageKey])
  const server = useQuery({
    queryKey: ['preference', companyId, preference],
    queryFn: () => api.get<UserPreference>(`/me/preferences/${preference}`),
    enabled: !!companyId,
    staleTime: 60_000,
  })
  const stored = typeof server.data?.value === 'string' ? server.data.value : undefined
  // Cerrado en otro equipo después que aquí: se adopta (y se cachea) el del servidor.
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al valor del servidor
  useEffect(() => {
    if (stored && (!since || stored > since)) {
      remember(stored)
      setSince(stored)
    }
  }, [stored])
  const save = useMutation({
    mutationFn: (value: string) => api.put<UserPreference>(`/me/preferences/${preference}`, { value }),
    // Si el servidor no responde, el cierre queda al menos en este navegador.
    onError: () => {},
  })
  const dismiss = () => {
    const now = new Date().toISOString()
    remember(now)
    setSince(now)
    if (companyId) save.mutate(now)
  }
  return { since, dismiss }
}

function ChartTip({
  active,
  payload,
  label,
  labelFormat,
}: {
  active?: boolean
  payload?: { name: string; value: number; color: string }[]
  label?: string | number
  labelFormat?: (l: string | number) => string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-elevation-3">
      <div className="mb-1 font-medium">
        {labelFormat && label !== undefined ? labelFormat(label) : label}
      </div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="size-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="ml-auto pl-4 font-medium tabular">{formatMoney(p.value)}</span>
        </div>
      ))}
    </div>
  )
}

function MonthlyChart({ data }: { data: DashboardData['monthly'] }) {
  const c = useChartColors()
  const chartMotion = useChartMotion()
  // Misma referencia mientras los datos no cambien: recharts no repite la animación en cada refetch.
  const rows = useMemo(() => data.map((m) => ({ ...m, label: MONTHS[Number(m.month.slice(5)) - 1] })), [data])
  return (
    <ResponsiveContainer>
      <BarChart data={rows} barGap={4} margin={{ left: -12, right: 4, top: 8 }}>
        <CartesianGrid vertical={false} stroke={c.grid} strokeDasharray="3 3" />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: c.muted, fontSize: 11 }} />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fill: c.muted, fontSize: 11 }}
          tickFormatter={(v) => formatCompact(Number(v))}
        />
        <ChartTooltip cursor={{ fill: c.grid, opacity: 0.4 }} content={<ChartTip />} />
        <Bar
          dataKey="sales"
          name="Ventas"
          fill={c.c1}
          radius={[6, 6, 0, 0]}
          maxBarSize={28}
          {...chartMotion}
        />
        <Bar
          dataKey="expenses"
          name="Gastos"
          fill={c.c2}
          radius={[6, 6, 0, 0]}
          maxBarSize={28}
          {...chartMotion}
          animationBegin={chartMotion.animationBegin + 60}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

function DailyChart({ data }: { data: DashboardData['daily'] }) {
  const c = useChartColors()
  const chartMotion = useChartMotion()
  const rows = useMemo(() => {
    let current = 0
    let previous = 0
    const today = new Date().getDate()
    return data.map((d) => {
      previous += d.previous
      if (d.day <= today) current += d.current
      return { day: d.day, current: d.day <= today ? current : null, previous }
    })
  }, [data])
  return (
    <ResponsiveContainer>
      <AreaChart data={rows} margin={{ left: -12, right: 4, top: 8 }}>
        <defs>
          <linearGradient id="dailyFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c.c1} stopOpacity={0.35} />
            <stop offset="100%" stopColor={c.c1} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={c.grid} strokeDasharray="3 3" />
        <XAxis
          dataKey="day"
          tickLine={false}
          axisLine={false}
          tick={{ fill: c.muted, fontSize: 11 }}
          interval={4}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fill: c.muted, fontSize: 11 }}
          tickFormatter={(v) => formatCompact(Number(v))}
        />
        <ChartTooltip content={<ChartTip labelFormat={(l) => `Día ${l}`} />} />
        <Area
          type="monotone"
          dataKey="previous"
          name="Mes anterior"
          stroke={c.muted}
          strokeDasharray="4 4"
          fill="none"
          strokeWidth={1.5}
          {...chartMotion}
        />
        <Area
          type="monotone"
          dataKey="current"
          name="Este mes"
          stroke={c.c1}
          fill="url(#dailyFill)"
          strokeWidth={2.25}
          connectNulls={false}
          {...chartMotion}
          animationBegin={chartMotion.animationBegin + 80}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

function RankList({
  items,
  empty,
}: {
  items: { label: string; value: number; hint: string }[]
  empty: string
}) {
  if (!items.length) return <p className="text-muted-foreground text-sm">{empty}</p>
  const max = Math.max(...items.map((i) => i.value), 1)
  return (
    <div className="grid gap-3">
      {items.map((item, index) => (
        <div key={item.label} className="grid gap-1.5">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-2">
              <span className="w-4 shrink-0 text-muted-foreground text-xs tabular">{index + 1}</span>
              <span className="truncate font-medium">{item.label}</span>
            </span>
            <span className="shrink-0 tabular">{formatMoney(item.value)}</span>
          </div>
          <div className="ml-6 h-1.5 overflow-hidden rounded-full bg-muted">
            {/* Crece con transform (scaleX) desde la izquierda; el ancho final lo fija el estilo. */}
            <m.div
              className="h-full origin-left rounded-full bg-brand-gradient transition-[width] duration-300 ease-smooth"
              style={{ width: `${(item.value / max) * 100}%` }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ ...TRANSITION.enter, duration: DURATION.slower, delay: 0.1 + index * STAGGER }}
            />
          </div>
          <span className="ml-6 text-muted-foreground text-xs">{item.hint}</span>
        </div>
      ))}
    </div>
  )
}

function SetupChecklist() {
  const company = useQuery(companyQuery())
  const hacienda = useQuery({
    queryKey: ['hacienda-settings'],
    queryFn: () => api.get<HaciendaSettings>('/hacienda/settings'),
  })
  const branches = useQuery({ queryKey: ['branches'], queryFn: () => api.get<Branch[]>('/branches') })
  const canSeeTeam = useCan('users:read')
  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<Member[]>('/members'),
    enabled: canSeeTeam,
  })
  if (!company.data || !hacienda.data) return null

  const steps = [
    {
      title: 'Datos del emisor',
      done: !!company.data.economicActivity && !!company.data.districtCode,
      to: '/settings/company' as const,
      icon: Building2Icon,
    },
    {
      title: 'Llave y credenciales de Hacienda',
      done: hacienda.data.ready || !company.data.haciendaEnabled,
      to: '/settings/hacienda' as const,
      icon: KeyRoundIcon,
    },
    {
      title: 'Sucursales y terminales',
      done: (branches.data?.length ?? 0) > 0,
      to: '/settings/branches' as const,
      icon: GitBranchIcon,
    },
    {
      title: 'Invitá a tu equipo',
      done: !canSeeTeam || (members.data?.length ?? 0) > 1,
      to: '/settings/team' as const,
      icon: UsersIcon,
    },
  ]
  const completed = steps.filter((s) => s.done).length
  if (completed === steps.length) return null

  return (
    <Card className="relative mb-6 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-50 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_60%)]" />
      <div className="relative flex flex-col gap-4 p-5 lg:flex-row lg:items-center">
        <div className="lg:w-64">
          <div className="flex items-center gap-2">
            <Badge variant="primary">
              {completed}/{steps.length}
            </Badge>
            <span className="font-semibold">Puesta en marcha</span>
          </div>
          <p className="mt-1 text-muted-foreground text-sm">
            Completá estos pasos para emitir comprobantes con validez fiscal.
          </p>
        </div>
        <div className="grid grid-cols-1 flex-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {steps.map((step) => (
            <Link
              key={step.title}
              to={step.to}
              className={cn(
                'group flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition',
                step.done
                  ? 'border-success/25 bg-success-soft/50'
                  : 'border-border bg-card hover:border-primary/40',
              )}
            >
              <step.icon
                className={cn('size-4 shrink-0', step.done ? 'text-success' : 'text-muted-foreground')}
              />
              <span className={cn('flex-1', step.done && 'text-muted-foreground line-through')}>
                {step.title}
              </span>
              {step.done ? (
                <CheckCircle2Icon className="size-4 text-success" />
              ) : (
                <CircleIcon className="size-4 text-border group-hover:text-primary" />
              )}
            </Link>
          ))}
        </div>
      </div>
    </Card>
  )
}
